import type { MarketoFormInstance } from '../marketo-form.types';

/**
 * Creates an arrow SVG element for the submit button
 */
const createArrowSvg = (): SVGElement => {
  const arrowSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  arrowSvg.setAttribute('width', '20');
  arrowSvg.setAttribute('height', '20');
  arrowSvg.setAttribute('viewBox', '0 0 24 24');
  arrowSvg.setAttribute('fill', 'none');
  arrowSvg.setAttribute('stroke', 'currentColor');
  arrowSvg.setAttribute('stroke-width', '2');
  arrowSvg.setAttribute('stroke-linecap', 'round');
  arrowSvg.setAttribute('stroke-linejoin', 'round');

  // Create the path element for the arrow
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M5 12h14m-7-7 7 7-7 7');

  arrowSvg.appendChild(path);
  return arrowSvg;
};

/**
 * Creates a button content wrapper div
 */
const createButtonContentWrapper = (submitButton: HTMLButtonElement): HTMLDivElement => {
  const buttonContent = document.createElement('div');
  buttonContent.className = 'mkto-button-content';

  // Move existing button content to the wrapper
  const existingContent = Array.from(submitButton.childNodes);
  existingContent.forEach((node) => {
    buttonContent.appendChild(node);
  });

  return buttonContent;
};

/**
 * Enhances the Marketo form submit button with custom styling and arrow icon.
 * Stores the original button label as a data attribute for later restoration.
 */
export const injectArrowIntoButton = (form: MarketoFormInstance): void => {
  const formEl = form.getFormElem()[0];
  const submitButton = formEl.querySelector('.mktoButton') as HTMLButtonElement;

  if (submitButton && !submitButton.querySelector('svg')) {
    // Store original label before wrapping
    if (!submitButton.dataset.originalLabel) {
      submitButton.dataset.originalLabel = submitButton.textContent?.trim() || 'Submit';
    }

    // Create a wrapper div for the button content (similar to button component)
    const buttonContent = createButtonContentWrapper(submitButton);

    // Create the arrow SVG element
    const arrowSvg = createArrowSvg();

    // Add the content wrapper and arrow to the button
    submitButton.appendChild(buttonContent);
    submitButton.appendChild(arrowSvg);
  }
};

/**
 * Restores the submit button to its original state after Marketo changes it
 * (e.g. to "Please Wait..." during submission).
 */
export const restoreSubmitButton = (form: MarketoFormInstance): void => {
  const formEl = form.getFormElem()[0];
  const submitButton = formEl.querySelector('.mktoButton') as HTMLButtonElement | null;

  if (!submitButton) return;

  const originalLabel = submitButton.dataset.originalLabel || 'Submit';

  // Clear Marketo's "Please Wait..." content and rebuild
  submitButton.innerHTML = '';
  submitButton.removeAttribute('disabled');

  const buttonContent = document.createElement('div');
  buttonContent.className = 'mkto-button-content';
  buttonContent.textContent = originalLabel;

  submitButton.appendChild(buttonContent);
  submitButton.appendChild(createArrowSvg());
};
